import { Card } from "@/components/ui/card";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Droplets } from "lucide-react";

interface FluidIntakeChartProps {
  bladderDiaryData: Array<{
    date: string;
    intakeEntries: Array<{ time: string; amount: number; type: string }>;
    urinationEntries: Array<{ time: string; urgency: number; hadLeakage: boolean; leakageAmount?: string; notes?: string }>;
  }>;
}

export const FluidIntakeChart = ({ bladderDiaryData }: FluidIntakeChartProps) => {
  const parseDateFlexible = (input: string): Date | null => {
    if (!input) return null;
    // ISO format: YYYY-MM-DD
    if (input.includes('-')) {
      const d = new Date(`${input}T00:00:00`);
      return isNaN(d.getTime()) ? null : d;
    }
    // Slash separated
    if (input.includes('/')) {
      const [a, b, c] = input.split('/').map((p) => parseInt(p, 10));
      if (!isNaN(a) && !isNaN(b) && !isNaN(c)) {
        // If first part > 12, it's day/month/year
        if (a > 12) {
          const d = new Date(c, b - 1, a);
          return isNaN(d.getTime()) ? null : d;
        }
        // If second part > 12, it's month/day/year
        if (b > 12) {
          const d = new Date(c, a - 1, b);
          return isNaN(d.getTime()) ? null : d;
        }
        // Fallback assume month/day/year
        const d = new Date(c, a - 1, b);
        return isNaN(d.getTime()) ? null : d;
      }
    }
    const d = new Date(input);
    return isNaN(d.getTime()) ? null : d;
  };
  // Calculate daily totals for the last 7 calendar days
  const today = new Date();
  const sevenDaysAgo = new Date();
  sevenDaysAgo.setDate(today.getDate() - 7);

  const chartData = bladderDiaryData
    .map(day => {
      const parsed = parseDateFlexible(day.date);
      if (!parsed) return null;

      // Only include entries from the last 7 days
      if (parsed < sevenDaysAgo || parsed > today) return null;

      const totalIntake = day.intakeEntries.reduce((sum, entry) => sum + entry.amount, 0);
      const bathroomTrips = day.urinationEntries.length;

      const formattedDate = parsed.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

      return {
        date: formattedDate,
        intake: totalIntake,
        trips: bathroomTrips,
      };
    })
    .filter((d): d is { date: string; intake: number; trips: number } => d !== null);


  // If no data, show empty state
  if (chartData.length === 0 || chartData.every(d => d.intake === 0 && d.trips === 0)) {
    return (
      <Card className="p-6 bg-gradient-to-br from-primary/5 to-accent/5 border border-border/50 rounded-2xl">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 rounded-lg bg-primary/10">
            <Droplets className="h-5 w-5 text-primary" />
          </div>
          <h3 className="text-lg font-semibold text-foreground">Daily Fluid Balance</h3>
        </div>
        <div className="text-center py-8 text-muted-foreground">
          <Droplets className="h-12 w-12 mx-auto mb-3 opacity-30" />
          <p className="text-sm">No bladder diary data available yet</p>
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-6 bg-gradient-to-br from-primary/5 to-accent/5 border border-border/50 rounded-2xl">
      <div className="flex items-center gap-3 mb-6">
        <div className="p-2 rounded-lg bg-primary/10">
          <Droplets className="h-5 w-5 text-primary" />
        </div>
        <h3 className="text-lg font-semibold text-foreground">Daily Fluid Balance</h3>
      </div>
      
      <ResponsiveContainer width="100%" height={280}>
        <BarChart data={chartData} margin={{ top: 5, right: 5, left: 5, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.3} />
          <XAxis 
            dataKey="date" 
            stroke="hsl(var(--muted-foreground))"
            fontSize={12}
            tickLine={false}
            tick={false}
          />
          <YAxis 
            stroke="hsl(var(--muted-foreground))"
            fontSize={12}
            tickLine={false}
            label={{ value: 'ml', angle: -90, position: 'insideLeft', style: { fill: 'hsl(var(--muted-foreground))' } }}
          />
          <Tooltip 
            contentStyle={{ 
              backgroundColor: 'hsl(var(--background))',
              border: '1px solid hsl(var(--border))',
              borderRadius: '8px',
              fontSize: '12px'
            }}
            formatter={(value: number, name: string) => {
              if (name === 'Bathroom Trips') {
                return [`${value} trips`, name];
              }
              return [`${value} ml`, name];
            }}
          />
          <Legend 
            wrapperStyle={{ fontSize: '12px' }}
            iconType="circle"
          />
          <Bar 
            dataKey="intake" 
            fill="hsl(var(--primary))" 
            radius={[8, 8, 0, 0]}
            name="Fluid Intake"
          />
          <Bar 
            dataKey="trips" 
            fill="hsl(var(--accent))" 
            radius={[8, 8, 0, 0]}
            name="Bathroom Trips"
          />
        </BarChart>
      </ResponsiveContainer>
      
      <div className="grid grid-cols-2 gap-4 mt-4 pt-4 border-t border-border/50">
        <div className="text-center">
          <p className="text-xs text-muted-foreground mb-1">Avg Intake</p>
          <p className="text-lg font-bold text-primary">
            {Math.round(chartData.reduce((sum, d) => sum + d.intake, 0) / chartData.length)} ml
          </p>
        </div>
        <div className="text-center">
          <p className="text-xs text-muted-foreground mb-1">Avg Bathroom Trips</p>
          <p className="text-lg font-bold text-accent">
            {Math.round(chartData.reduce((sum, d) => sum + d.trips, 0) / chartData.length)} trips/day
          </p>
        </div>
      </div>
    </Card>
  );
};
